/*
 * PDP image preloading (snippets/product-media-gallery.liquid).
 * The gallery slides themselves are fetched and decoded by the carousel (assets/media-gallery.js:
 * next two slides once the main image has loaded, the rest on idle or on intent). This script adds:
 *   - the color swatch images, in the background once the page is idle (two at a time, low
 *     priority; skipped on Save-Data and 2G)
 *   - a fast path: hovering or focusing a thumbnail or a color swatch fetches and decodes that
 *     slide's real <img> at once (also on slow connections: the shopper asked for it), so the
 *     click that follows switches to an image that is already there
 */
(() => {
  const started = new WeakSet();
  const queue = [];
  let active = 0;
  const LIMIT = 2;

  const conn = navigator.connection || {};
  const slow = conn.saveData || /(^|-)2g$/.test(conn.effectiveType || '');

  function fetchImage(img, done) {
    if (!img || started.has(img)) return done && done();
    started.add(img);
    // already in view and loaded, or nothing to fetch
    if ((img.complete && img.naturalWidth > 0) || !(img.currentSrc || img.src || img.getAttribute('srcset'))) {
      return done && done();
    }
    const pre = new Image();
    pre.decoding = 'async';
    pre.fetchPriority = 'low';
    pre.setAttribute('fetchpriority', 'low');
    // sizes before srcset, so the first candidate chosen is the right one
    if (img.getAttribute('sizes')) pre.sizes = img.getAttribute('sizes');
    if (img.getAttribute('srcset')) pre.srcset = img.getAttribute('srcset');
    pre.src = img.getAttribute('src') || '';
    const finish = () => done && done();
    pre.onload = finish;
    pre.onerror = finish;
  }

  function pump() {
    while (active < LIMIT && queue.length) {
      const img = queue.shift();
      if (started.has(img)) continue;
      active += 1;
      fetchImage(img, () => {
        active -= 1;
        pump();
      });
    }
  }

  // prime a gallery slide's own <img> (the carousel fetches and decodes it in place)
  const primeSlide = (gallery, mediaId) => {
    const li = gallery && gallery.querySelector(`[data-media-id="${mediaId}"]`);
    if (!li) return;
    if (gallery.carousel) gallery.carousel.prime(li);
    else {
      const img = li.querySelector('.product__media img');
      if (img && img.loading === 'lazy') img.loading = 'eager';
    }
  };

  function plan() {
    document.querySelectorAll('.jc-cswatch__img').forEach((img) => !queue.includes(img) && queue.push(img));
    pump();
  }

  const dataCache = new WeakMap();
  function readData(gallery) {
    if (dataCache.has(gallery)) return dataCache.get(gallery);
    const id = gallery.id.replace('MediaGallery-', '');
    const el = document.querySelector(`script[data-jc-variant-media="${id}"]`);
    let data = {};
    try {
      data = JSON.parse(el ? el.textContent : '{}');
    } catch (e) {
      /* malformed: preload the gallery only */
    }
    dataCache.set(gallery, data);
    return data;
  }

  function whenIdle(fn) {
    const go = () => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 2000 }) : setTimeout(fn, 1500));
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
  }

  function init() {
    const galleries = document.querySelectorAll('media-gallery[id^="MediaGallery-"]');
    if (!galleries.length) return;

    if (!slow) whenIdle(plan);

    // fast path: a hovered or focused thumbnail or swatch loads its image right away
    const fast = (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      const thumb = t.closest('[data-target]');
      if (thumb) return primeSlide(thumb.closest('media-gallery'), thumb.dataset.target);
      const label = t.closest('label.jc-cswatch');
      const input = label ? document.getElementById(label.htmlFor) : t.matches('input[type="radio"]') ? t : null;
      if (!input) return;
      galleries.forEach((gallery) => {
        const map = readData(gallery).byColor || {};
        if (map[input.value]) primeSlide(gallery, map[input.value]);
      });
    };
    document.addEventListener('mouseover', fast, { passive: true });
    document.addEventListener('touchstart', fast, { passive: true });
    document.addEventListener('focusin', fast);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
